
import React from 'react';
import Box from '../../components/Common/Box';
import Disclaimer from '../../components/Common/Disclaimer';

interface DesktopPrudentProps {
    data: any;
    activeTab: string;
    setActiveTab: (tab: any) => void;
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    activeFilters: string[];
    toggleFilter: (filter: string) => void;
    opportunities: any[];
    sellSignals: any[];
    filteredUniverse: any[];
    sentiment: string;
    sentimentColors: any;
    navigate: (path: string) => void;
}

const DesktopPrudent: React.FC<DesktopPrudentProps> = ({
    data,
    activeTab,
    setActiveTab,
    searchTerm,
    setSearchTerm,
    activeFilters,
    toggleFilter,
    opportunities,
    sellSignals,
    filteredUniverse,
    sentiment,
    sentimentColors,
    navigate
}) => {
    return (
        <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
            <Disclaimer type="investment" />

            {/* Simplified Summary */}
            <Box>
                <div style={styles.summaryGrid}>
                    <div style={styles.summaryItem}>
                        <span style={styles.summaryLabel}>📊 Opportunités détectées</span>
                        <span style={styles.summaryValue}>{opportunities.length}</span>
                    </div>
                    <div style={styles.summaryItem}>
                        <span style={styles.summaryLabel}>📈 Sentiment marché</span>
                        <span style={{
                            ...styles.badge,
                            backgroundColor: sentimentColors[sentiment].bg,
                            color: sentimentColors[sentiment].color,
                        }}>
                            {sentiment === 'BULLISH' ? '🟢 Haussier' : sentiment === 'BEARISH' ? '🔴 Baissier' : '⚪ Neutre'}
                        </span>
                    </div>
                </div>
            </Box>

            {/* Tabs */}
            <div style={styles.tabContainer}>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'opportunities' ? styles.tabActive : {}) }}
                    onClick={() => setActiveTab('opportunities')}
                >
                    🔥 Configurations Haussières ({opportunities.length})
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'sell' ? styles.tabActiveSell : {}) }}
                    onClick={() => setActiveTab('sell')}
                >
                    📉 Alertes Baissières ({sellSignals.length})
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'universe' ? styles.tabActiveUniverse : {}) }}
                    onClick={() => setActiveTab('universe')}
                >
                    🌍 Univers ({data?.eligible_universe?.length || 0})
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'info' ? styles.tabActiveInfo : {}) }}
                    onClick={() => setActiveTab('info')}
                >
                    ℹ️ Stratégie Applicable
                </button>
            </div>

            {/* Opportunities */}
            {activeTab === 'opportunities' && (
                <div style={styles.cardsGrid}>
                    {opportunities.length === 0 ? (
                        <Box>
                            <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                                <p>Aucune action en zone de surenvente (Stoch &lt; 20) actuellement.</p>
                                <p style={{ fontSize: 14 }}>Revenez plus tard, la patience est la clé de cette stratégie.</p>
                            </div>
                        </Box>
                    ) : (
                        opportunities.map((opp) => (
                            <Box key={opp.symbole}>
                                <div style={styles.card}>
                                    <div style={styles.cardHeader}>
                                        <div>
                                            <span style={styles.symbolLink} onClick={() => navigate(`/data?symbol=${opp.symbole}`)}>
                                                {opp.symbole}
                                            </span>
                                            <p style={styles.companyName}>{opp.nom_societe}</p>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ ...styles.scoreBadge, backgroundColor: '#16a34a' }}>
                                                {opp.score}
                                            </div>
                                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Score</span>
                                        </div>
                                    </div>

                                    <div style={styles.priceGrid}>
                                        <div>
                                            <span style={styles.priceLabel}>Prix Actuel</span>
                                            <span style={styles.priceValue}>{opp.entry_price.toLocaleString()}</span>
                                        </div>
                                        <div>
                                            <span style={styles.priceLabel}>Stoch %K</span>
                                            <span style={{ ...styles.priceValue, color: '#16a34a' }}>{opp.stoch_k?.toFixed(0)}</span>
                                        </div>
                                        <div>
                                            <span style={styles.priceLabel}>RSI</span>
                                            <span style={{ ...styles.priceValue, color: (opp.rsi || 50) < 30 ? '#16a34a' : (opp.rsi || 50) > 70 ? 'var(--text-danger, #dc2626)' : 'var(--text-muted)' }}>
                                                {opp.rsi?.toFixed(0) || '-'}
                                            </span>
                                        </div>
                                        <div>
                                            <span style={styles.priceLabel}>Vol. Moyen</span>
                                            <span style={{ ...styles.priceValue, color: (opp.avg_volume || 0) > 1000000 ? '#16a34a' : 'var(--text-muted)' }}>
                                                {(opp.avg_volume ? (opp.avg_volume / 1000000).toFixed(1) : '0')}M
                                            </span>
                                        </div>
                                        <div>
                                            <span style={styles.priceLabel}>Stoch %D</span>
                                            <span style={{ ...styles.priceValue, color: 'var(--text-muted)' }}>{opp.stoch_d?.toFixed(0)}</span>
                                        </div>
                                    </div>

                                    <p style={styles.justification}>{opp.justification}</p>
                                </div>
                            </Box>
                        ))
                    )}
                </div>
            )}

            {/* Sell Signals */}
            {activeTab === 'sell' && (
                <div style={styles.cardsGrid}>
                    {sellSignals.length === 0 ? (
                        <Box>
                            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                                <p>Aucune alerte de surachat détectée sur les positions en cours.</p>
                            </div>
                        </Box>
                    ) : (
                        sellSignals.map((signal) => (
                            <Box key={signal.symbole}>
                                <div style={styles.card}>
                                    <div style={styles.cardHeader}>
                                        <div>
                                            <span style={styles.symbolLink} onClick={() => navigate(`/data?symbol=${signal.symbole}`)}>
                                                {signal.symbole}
                                            </span>
                                            <p style={styles.companyName}>{signal.nom_societe}</p>
                                        </div>
                                        <span style={{ ...styles.badge, backgroundColor: 'rgba(220, 38, 38, 0.1)', color: 'var(--text-danger, #dc2626)' }}>
                                            ALERTE SORTIE
                                        </span>
                                    </div>

                                    <div style={styles.pnlInfo}>
                                        <div>
                                            <span style={styles.priceLabel}>Gain/Perte</span>
                                            <span style={{ ...styles.priceValue, color: signal.unrealized_pnl_pct >= 0 ? '#16a34a' : 'var(--text-danger, #dc2626)' }}>
                                                {signal.unrealized_pnl_pct > 0 ? '+' : ''}{signal.unrealized_pnl_pct.toFixed(2)}%
                                            </span>
                                        </div>
                                    </div>

                                    <p style={styles.justification}>{signal.justification}</p>

                                    <div style={styles.actionRow}>
                                        <span style={{ ...styles.actionBadge, backgroundColor: 'var(--text-danger, #dc2626)', color: 'white' }}>
                                            {signal.recommended_action === 'SELL_ALL' ? 'ALERTE SORTIE' : 'SORTIE PARTIELLE'}
                                        </span>
                                    </div>
                                </div>
                            </Box>
                        )
                    ))}
                </div>
            )}

            {/* Universe */}
            {activeTab === 'universe' && (
                <Box>
                    {/* Search & Filters */}
                    <div style={styles.filterBar}>
                        <div style={styles.searchContainer}>
                            <span style={{ marginRight: 8 }}>🔍</span>
                            <input
                                type="text"
                                placeholder="Rechercher un titre..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={styles.searchInput}
                            />
                        </div>
                        <div style={styles.filterChips}>
                            <button
                                style={{ ...styles.filterChip, ...(activeFilters.includes('rsi70') ? styles.filterChipActive : {}) }}
                                onClick={() => toggleFilter('rsi70')}
                            >
                                📉 RSI &lt; 70
                            </button>
                            <button
                                style={{ ...styles.filterChip, ...(activeFilters.includes('stoch20') ? styles.filterChipActive : {}) }}
                                onClick={() => toggleFilter('stoch20')}
                            >
                                📊 Stoch &lt; 20
                            </button>
                            <button
                                style={{ ...styles.filterChip, ...(activeFilters.includes('liquid') ? styles.filterChipActive : {}) }}
                                onClick={() => toggleFilter('liquid')}
                            >
                                💧 Vol. &gt; 2M
                            </button>
                            <button
                                style={{ ...styles.filterChip, ...(activeFilters.includes('undervalued') ? styles.filterChipActive : {}) }}
                                onClick={() => toggleFilter('undervalued')}
                            >
                                💎 Sous-Évaluée
                            </button>
                            <button
                                style={{ ...styles.filterChip, ...(activeFilters.includes('obvBullish') ? styles.filterChipActive : {}) }}
                                onClick={() => toggleFilter('obvBullish')}
                            >
                                📈 OBV Haussier
                            </button>
                            <button
                                style={{ ...styles.filterChip, ...(activeFilters.includes('goldenCross') ? styles.filterChipActive : {}) }}
                                onClick={() => toggleFilter('goldenCross')}
                            >
                                🏆 Golden Cross
                            </button>
                        </div>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Symbole</th>
                                    <th style={styles.th}>Nom</th>
                                    <th style={styles.thCenter}>Cours</th>
                                    <th style={styles.thCenter}>Stoch %K</th>
                                    <th style={styles.thCenter}>Stoch %D</th>
                                    <th style={styles.thCenter}>RSI</th>
                                    <th style={styles.thCenter}>GC</th>
                                    <th style={styles.th}>Analyse</th>
                                    <th style={styles.thCenter}>Statut</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUniverse.map((stock) => (
                                    <tr key={stock.symbole} style={{
                                        backgroundColor: stock.is_opportunity ? 'rgba(22, 163, 74, 0.1)' : stock.is_owned ? 'rgba(14, 165, 233, 0.1)' : 'transparent'
                                    }}>
                                        <td style={styles.td}>
                                            <span style={styles.symbolLink} onClick={() => navigate(`/data?symbol=${stock.symbole}`)}>
                                                {stock.symbole}
                                            </span>
                                        </td>
                                        <td style={styles.td}>{stock.nom_societe}</td>
                                        <td style={styles.tdCenter}>{stock.cours_actuel?.toLocaleString()}</td>
                                        <td style={{ ...styles.tdCenter, fontWeight: 'bold', color: stock.stoch_k < 20 ? '#16a34a' : stock.stoch_k > 80 ? 'var(--text-danger, #dc2626)' : 'var(--text-main, #374151)' }}>
                                            {stock.stoch_k?.toFixed(0)}
                                        </td>
                                        <td style={styles.tdCenter}>{stock.stoch_d?.toFixed(0)}</td>
                                        <td style={styles.tdCenter}>{stock.rsi?.toFixed(0)}</td>
                                        <td style={styles.tdCenter}>
                                            {stock.is_golden_cross ? '🏆' : '-'}
                                        </td>
                                        <td style={styles.tdAnalysis}>{stock.analysis}</td>
                                        <td style={styles.tdCenter}>
                                            {stock.is_opportunity ? '🔥 Opportunité' : stock.is_owned ? '📍 Détenu' : '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Box>
            )}

            {/* Info */}
            {activeTab === 'info' && (
                <Box>
                    <div style={{ padding: 20 }}>
                        <h3>🐢 Stratégie Applicable : "Investisseur Prudent" (Mean Reversion)</h3>
                        <p>Cette stratégie utilise l'oscillateur Stochastique pour identifier les points de retournement dans un marché en range (comme la BRVM).</p>

                        <h4 style={{ marginTop: 20 }}>✅ Configuration Haussière</h4>
                        <ul>
                            <li><strong>Filtre Fondamental :</strong> Action classée "SOUS-ÉVALUÉE" (Valuation Cache) avec Potentiel &gt; 20%.</li>
                            <li><strong>Trigger :</strong> Stochastique (14, 3, 3) %K inférieur à 20 (Zone de survente).</li>
                            <li><strong>Filtre RSI :</strong> RSI inférieur à 70 (Pas de surachat).</li>
                            <li><strong>Confirmation :</strong> OBV en hausse (Accumulation) ou cassure de résistance.</li>
                        </ul>

                        <h4 style={{ marginTop: 20 }}>❌ Alerte de Sortie</h4>
                        <ul>
                            <li>Stochastique %K supérieur à 80 (Zone de surachat) ou Perte de Momentum.</li>
                            <li><strong>Stop Loss : </strong> -10% environ (ou cassure du support récent).</li>
                            <li><strong>Pas de limite de gains :</strong> Tant que le titre ne dépasse pas 80, on laisse courir les profits !</li>
                        </ul>
                    </div>
                </Box>
            )}
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16, padding: 20 },
    summaryItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' },
    summaryLabel: { fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 },
    summaryValue: { fontSize: 20, fontWeight: 'bold', color: 'var(--text-heading)' },
    badge: { padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: '600' },
    tabContainer: { display: 'flex', gap: 8, marginBottom: 20, marginTop: 20 },
    tab: { flex: 1, padding: '12px 16px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: '600', cursor: 'pointer', backgroundColor: 'var(--bg-box)', color: 'var(--text-main)' },
    tabActive: { backgroundColor: '#16a34a', color: 'white' },
    tabActiveSell: { backgroundColor: '#dc2626', color: 'white' },
    tabActiveUniverse: { backgroundColor: '#7c3aed', color: 'white' },
    tabActiveInfo: { backgroundColor: '#2563eb', color: 'white' },
    cardsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 16 },
    card: { padding: 16, backgroundColor: 'var(--bg-box)', borderRadius: 12, border: '1px solid var(--border-color)' },
    cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
    symbolLink: { fontSize: 18, fontWeight: 'bold', color: '#2563eb', cursor: 'pointer' },
    companyName: { fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 0 0' },
    scoreBadge: { width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: 16 },
    priceGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12, padding: 12, backgroundColor: 'var(--bg-input)', borderRadius: 8 },
    priceLabel: { display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 },
    priceValue: { display: 'block', fontSize: 14, fontWeight: '600', color: 'var(--text-heading)' },
    justification: { fontSize: 13, color: 'var(--text-muted)', margin: 0, fontStyle: 'italic' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { textAlign: 'left', padding: '12px 8px', borderBottom: '2px solid var(--border-color)', fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap' },
    thCenter: { textAlign: 'center', padding: '12px 8px', borderBottom: '2px solid var(--border-color)', fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap' },
    td: { padding: '10px 8px', borderBottom: '1px solid var(--border-color)', verticalAlign: 'middle', color: 'var(--text-main)' },
    tdCenter: { padding: '10px 8px', borderBottom: '1px solid var(--border-color)', textAlign: 'center', color: 'var(--text-main)' },
    tdAnalysis: { padding: '10px 8px', borderBottom: '1px solid var(--border-color)', fontSize: 12, color: 'var(--text-muted)', maxWidth: 300 },
    pnlInfo: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 12, padding: 12, backgroundColor: 'var(--bg-input)', borderRadius: 8 },
    actionRow: { marginTop: 12, textAlign: 'center' },
    actionBadge: { padding: '8px 16px', borderRadius: 6, fontSize: 14, fontWeight: '600' },
    filterBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 16 },
    searchContainer: { display: 'flex', alignItems: 'center', backgroundColor: 'var(--bg-box)', padding: '8px 16px', borderRadius: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', flex: 1, minWidth: 200 },
    searchInput: { border: 'none', outline: 'none', width: '100%', fontSize: 14, backgroundColor: 'transparent', color: 'var(--text-main)' },
    filterChips: { display: 'flex', gap: 8 },
    filterChip: { padding: '6px 16px', borderRadius: 20, border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-box)', cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)', transition: 'all 0.2s' },
    filterChipActive: { backgroundColor: 'rgba(37, 99, 235, 0.1)', color: '#2563eb', borderColor: '#2563eb', fontWeight: '600' },
};

export default DesktopPrudent;
